<template>
  <div class="camera-recorder">
    <h2>4K Camera Recorder</h2>

    <div v-if="!isApiSupported" class="error">
      Required APIs (getUserMedia, MediaRecorder, MediaCapabilities) are not fully supported by your browser.
    </div>

    <div v-else>
      <div class="status">
        <p>Status: {{ statusMessage }}</p>
        <p v-if="capabilityCheckResult !== null">
          4K High-Bitrate Recording Supported:
          <span :class="is4kSupported ? 'supported' : 'not-supported'">
            {{ is4kSupported ? 'Yes' : 'No' }}
            <template v-if="is4kSupported && supportedMimeType"> (Using: {{ supportedMimeType }})</template>
            <template v-if="!is4kSupported && capabilityCheckResult"> (Reason: {{ capabilityCheckResult.reason || 'Unsupported configuration' }})</template>
          </span>
        </p>
      </div>

      <div class="controls">
        <button @click="checkRecordingCapabilities" :disabled="isLoading || isRecording">
          Check 4K Support
        </button>
        <button @click="startRecording" :disabled="isLoading || isRecording || !is4kSupported" v-if="!isRecording">
          Start 4K Recording
        </button>
        <button @click="stopRecording" :disabled="!isRecording" v-if="isRecording">
          Stop Recording
        </button>
      </div>

      <div class="video-container">
        <h3>Preview</h3>
        <video ref="videoPreviewRef" autoplay muted playsinline></video>
      </div>

      <div v-if="recordedVideoUrl" class="download-section">
        <h3>Recording Complete</h3>
        <p>Download your recording:</p>
        <a :href="recordedVideoUrl" :download="downloadFilename" ref="downloadLinkRef">
          {{ downloadFilename }}
        </a>
        <p>(File size might be very large)</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';

// --- Configuration ---
const VIDEO_WIDTH = 3840;
const VIDEO_HEIGHT = 2160;
const VIDEO_FRAMERATE = 30; // Desired framerate
// Aim for high quality ~50Mbps. Adjust as needed.
const VIDEO_BITRATE = 50 * 1000 * 1000;
// Try preferred codecs first (VP9/AV1 often better quality/efficiency)
const PREFERRED_MIME_TYPES = [
  'video/webm;codecs=vp9',
  'video/webm;codecs=av01', // AV1 encoding support is less common
  'video/mp4;codecs=h264', // More common fallback
  'video/webm;codecs=vp8', // Older fallback
  // Add more specific types if needed, e.g., with profile/level
  // 'video/mp4; codecs="avc1.640028"', // H.264 High Profile Level 4.0
];

// --- Reactive State ---
const videoPreviewRef = ref(null);
const downloadLinkRef = ref(null);
const mediaStream = ref(null);
const mediaRecorder = ref(null);
const recordedChunks = ref([]);
const recordedVideoUrl = ref(null);
const isRecording = ref(false);
const isLoading = ref(false); // For async operations like capability check/getUserMedia
const statusMessage = ref('Idle. Check support first.');
const capabilityCheckResult = ref(null); // Stores the raw result or error reason
const supportedMimeType = ref(''); // Stores the mimeType confirmed by MediaCapabilities

// --- Computed Properties ---
const isApiSupported = computed(() =>
  'mediaDevices' in navigator &&
  'getUserMedia' in navigator.mediaDevices &&
  'MediaRecorder' in window &&
  'mediaCapabilities' in navigator
);

const is4kSupported = computed(() =>
    capabilityCheckResult.value?.supported ?? false
);

const downloadFilename = computed(() => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const extension = supportedMimeType.value?.split(';')[0].split('/')[1] || 'webm';
    return `recording-4k-${timestamp}.${extension}`;
});


// --- Methods ---

// 1. Check Media Capabilities
async function checkRecordingCapabilities() {
  if (!isApiSupported.value) {
    statusMessage.value = "Browser lacks necessary API support.";
    return;
  }

  isLoading.value = true;
  statusMessage.value = 'Checking 4K recording capabilities...';
  capabilityCheckResult.value = null; // Reset previous check
  supportedMimeType.value = '';

  let foundSupportedConfig = false;

  for (const mimeType of PREFERRED_MIME_TYPES) {
    const videoConfig = {
      contentType: mimeType,
      width: VIDEO_WIDTH,
      height: VIDEO_HEIGHT,
      bitrate: VIDEO_BITRATE,
      framerate: VIDEO_FRAMERATE,
    };

    try {
      // Check encoding capabilities
      const result = await navigator.mediaCapabilities.encodingInfo({
        type: 'record', // Check for recording, 'transmission' also possible
        video: videoConfig,
        // audio: { // Optionally check audio encoding too
        //   contentType: 'audio/opus', // Example
        //   channels: 2,
        //   bitrate: 128000,
        //   samplerate: 48000
        // }
      });

      console.log(`Checking ${mimeType}:`, result);

      if (result.supported) {
        capabilityCheckResult.value = { supported: true, config: videoConfig, powerEfficient: result.powerEfficient, smooth: result.smooth };
        supportedMimeType.value = mimeType; // Store the supported type
        statusMessage.value = `4K recording check complete. Supported (${mimeType}).`;
        foundSupportedConfig = true;
        break; // Stop checking once a supported config is found
      }
    } catch (error) {
      console.error(`Error checking capabilities for ${mimeType}:`, error);
       // Store the reason for the last failure if none are supported
      if (!foundSupportedConfig) {
          capabilityCheckResult.value = { supported: false, reason: `${mimeType}: ${error.message}` };
      }
    }
  }

  if (!foundSupportedConfig) {
     statusMessage.value = '4K recording check complete. No suitable configuration found.';
     if (!capabilityCheckResult.value) { // Handle case where loop didn't run or all threw errors early
         capabilityCheckResult.value = { supported: false, reason: 'Failed to check any configuration.' };
     }
  }

  isLoading.value = false;
}

// 2. Start Recording
async function startRecording() {
  if (!is4kSupported.value || !supportedMimeType.value) {
    statusMessage.value = "Cannot start: 4K recording not supported or not checked.";
    return;
  }
  if (isRecording.value) return;

  // Clear previous recording
  recordedVideoUrl.value = null;
  recordedChunks.value = [];

  isLoading.value = true;
  statusMessage.value = 'Requesting camera access...';

  try {
    // Request 4K video stream
    mediaStream.value = await navigator.mediaDevices.getUserMedia({
      audio: true, // Include audio
      video: {
        width: { ideal: VIDEO_WIDTH },   // Use 'exact' to enforce 4K
        height: { ideal: VIDEO_HEIGHT },
        frameRate: { ideal: VIDEO_FRAMERATE } // 'ideal' is less strict than 'exact'
      }
    });

    statusMessage.value = 'Camera access granted. Starting recorder...';

    // Assign stream to preview video element
    if (videoPreviewRef.value) {
      videoPreviewRef.value.srcObject = mediaStream.value;
      videoPreviewRef.value.captureStream = videoPreviewRef.value.captureStream || videoPreviewRef.value.mozCaptureStream; // For Firefox compatibility if needed later
      await videoPreviewRef.value.play(); // Ensure playback starts for preview
    }

    // Create MediaRecorder instance
    const options = {
        mimeType: supportedMimeType.value, // Use the validated mimeType
        videoBitsPerSecond: VIDEO_BITRATE // Specify the high bitrate
        // audioBitsPerSecond: 128000 // Optional: Specify audio bitrate
    };

    // Verify MediaRecorder actually supports the mimeType (sometimes differs from mediaCapabilities)
    if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        console.warn(`MediaRecorder does not support ${options.mimeType}, falling back or failing.`);
        // Optional: Try a fallback mimeType here if needed
        // For now, we'll throw an error
        throw new Error(`MediaRecorder reported unsupported mimeType: ${options.mimeType}`);
    }

    mediaRecorder.value = new MediaRecorder(mediaStream.value, options);

    // Event Handlers for MediaRecorder
    mediaRecorder.value.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        recordedChunks.value.push(event.data);
      }
    };

    mediaRecorder.value.onstop = () => {
      statusMessage.value = 'Recording stopped. Processing video...';
      const blob = new Blob(recordedChunks.value, { type: supportedMimeType.value });
      recordedVideoUrl.value = URL.createObjectURL(blob);
      isRecording.value = false;
      isLoading.value = false;
      statusMessage.value = 'Processing complete. Download ready.';
       // Clean up the stream tracks ONLY after recorder is fully stopped and blob created
       if (mediaStream.value) {
           mediaStream.value.getTracks().forEach(track => track.stop());
           mediaStream.value = null; // Release reference
       }
        if (videoPreviewRef.value) {
            videoPreviewRef.value.srcObject = null; // Clear preview
        }

    };

    mediaRecorder.value.onerror = (event) => {
      console.error("MediaRecorder Error:", event.error);
      statusMessage.value = `Recording Error: ${event.error.name || 'Unknown error'}`;
      isRecording.value = false;
      isLoading.value = false;
      // Attempt cleanup
      if (mediaStream.value) {
          mediaStream.value.getTracks().forEach(track => track.stop());
          mediaStream.value = null;
      }
        if (videoPreviewRef.value) {
            videoPreviewRef.value.srcObject = null; // Clear preview
        }
    };

    // Start recording
    mediaRecorder.value.start(); // Optional: pass timeslice (e.g., 1000) for ondataavailable chunks
    isRecording.value = true;
    isLoading.value = false; // Loading finished when recording starts
    statusMessage.value = 'Recording 4K video...';

  } catch (error) {
    console.error("Error starting recording:", error);
    let message = 'Failed to start recording.';
    if (error.name === 'NotAllowedError') {
      message = 'Permission denied. Please allow camera/microphone access.';
    } else if (error.name === 'NotFoundError') {
      message = 'No camera/microphone found.';
    } else if (error.name === 'NotReadableError') {
      message = 'Camera/microphone is already in use or hardware error.';
    } else if (error.name === 'OverconstrainedError') {
        message = `Could not satisfy constraints (e.g., 4K resolution not supported by device): ${error.constraint}`;
    } else if (error.message.includes("MediaRecorder reported unsupported mimeType")){
        message = error.message; // Use the specific error from isTypeSupported check
    }
    statusMessage.value = message;
    isLoading.value = false;
    isRecording.value = false;
     // Clean up if stream was partially acquired
     if (mediaStream.value) {
       mediaStream.value.getTracks().forEach(track => track.stop());
       mediaStream.value = null;
     }
  }
}

// 3. Stop Recording
function stopRecording() {
  if (!isRecording.value || !mediaRecorder.value) return;

  statusMessage.value = 'Stopping recording...';
  mediaRecorder.value.stop(); // This triggers the 'onstop' handler
  // Stream tracks are stopped in the onstop handler after blob creation
}

// --- Lifecycle Hooks ---
onMounted(() => {
  // Optional: Automatically check capabilities on component mount
  // checkRecordingCapabilities();
});

onUnmounted(() => {
  // Cleanup: Stop recording, release camera, revoke object URL
  if (isRecording.value && mediaRecorder.value) {
    mediaRecorder.value.stop();
  }
  if (mediaStream.value) {
    mediaStream.value.getTracks().forEach(track => track.stop());
  }
  if (recordedVideoUrl.value) {
    URL.revokeObjectURL(recordedVideoUrl.value);
  }
});

</script>

<style scoped>
.camera-recorder {
  font-family: sans-serif;
  padding: 20px;
  border: 1px solid #ccc;
  border-radius: 8px;
  max-width: 800px;
  margin: 20px auto;
}

.status p {
  margin: 5px 0;
  font-weight: bold;
}

.status .supported {
  color: green;
}

.status .not-supported {
  color: red;
}

.controls button {
  margin: 10px 5px 10px 0;
  padding: 10px 15px;
  font-size: 1em;
  cursor: pointer;
  border-radius: 4px;
  border: 1px solid #ddd;
  background-color: #f0f0f0;
}

.controls button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.controls button:not(:disabled):hover {
  background-color: #e0e0e0;
}

.video-container {
  margin-top: 20px;
  background-color: #000;
}

.video-container h3 {
    color: #eee;
    padding: 5px;
    margin: 0;
}

video {
  display: block; /* Prevents extra space below video */
  width: 100%;
  max-height: 450px; /* Limit preview height */
  background-color: #222; /* Dark background for video area */
}

.download-section {
  margin-top: 20px;
  padding: 15px;
  background-color: #e8f4e8;
  border: 1px solid #b8d8b8;
  border-radius: 4px;
}

.download-section a {
  font-weight: bold;
  word-break: break-all; /* Handle long filenames */
}

.error {
    color: #D8000C;
    background-color: #FFD2D2;
    border: 1px solid #D8000C;
    padding: 10px;
    border-radius: 4px;
    margin-bottom: 15px;
}
</style>
